import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import { mainContent, rirekishoContent } from '@src/constants/path_list';
import { RouterPath } from '@src/constants/router_path';
import type { TranslationKeys } from '@src/types/i18n';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export type MenuItem = {
  label: string;
  path: string;
  translationKey?: TranslationKeys;
  relatedPaths?: string[];
};

export const useMenuData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState<string>('');

  const isRirekishoEnabled = useFeatureFlag(FeatureFlag.USE_RIREKISHO);
  const isResumeEnabled = useFeatureFlag(FeatureFlag.USE_RESUME);

  // 前のパスを記録するためのuseEffect
  useEffect(() => {
    const currentPath = location.pathname;

    // 現在のパスが履歴書または経歴書のページの場合、前のパスとして記録
    if (
      (isRirekishoEnabled && rirekishoContent.some((item) => item.path === currentPath)) ||
      (isResumeEnabled && mainContent.some((item) => item.path === currentPath))
    ) {
      setPreviousPath(currentPath);
    }
  }, [location.pathname, isRirekishoEnabled, isResumeEnabled]);

  // 表示するメニューを動的に計算
  const currentMainContent = useMemo((): MenuItem[] => {
    const path = location.pathname;

    const isRirekishoPage =
      isRirekishoEnabled && rirekishoContent.some((item) => item.path === path);
    const isResumePage = isResumeEnabled && mainContent.some((item) => item.path === path);
    const isUtilityPage = [
      RouterPath.faq,
      RouterPath.exportImport,
      RouterPath.index,
      RouterPath.selectMode,
    ].includes(path as RouterPath);

    const createMenuItems = (
      sourceList: typeof mainContent | typeof rirekishoContent
    ): MenuItem[] => {
      const grouped = new Map<string, MenuItem>();

      for (const item of sourceList) {
        const translationKey = item.name as TranslationKeys;
        const existing = grouped.get(item.name);

        if (existing) {
          existing.relatedPaths = Array.from(
            new Set([...(existing.relatedPaths ?? []), item.path])
          );
        } else {
          grouped.set(item.name, {
            label: t(translationKey),
            path: item.path,
            translationKey,
            relatedPaths: [item.path],
          });
        }
      }

      return Array.from(grouped.values());
    };

    if (isRirekishoPage) {
      localStorage.setItem('lastContentType', 'rirekisho');
      return createMenuItems(rirekishoContent);
    }

    if (isResumePage) {
      localStorage.setItem('lastContentType', 'resume');
      return createMenuItems(mainContent);
    }

    if (isUtilityPage) {
      // デフォルトは有効な方を選択（両方有効なら経歴書優先）
      const defaultContentType = isResumeEnabled ? 'resume' : isRirekishoEnabled ? 'rirekisho' : '';
      let lastContentType = localStorage.getItem('lastContentType') || defaultContentType;

      if (previousPath) {
        if (isRirekishoEnabled && rirekishoContent.some((item) => item.path === previousPath)) {
          lastContentType = 'rirekisho';
          localStorage.setItem('lastContentType', 'rirekisho');
        } else if (isResumeEnabled && mainContent.some((item) => item.path === previousPath)) {
          lastContentType = 'resume';
          localStorage.setItem('lastContentType', 'resume');
        }
      }

      // 選択されたタイプが無効な場合は、有効な方に切り替え
      if (lastContentType === 'rirekisho' && !isRirekishoEnabled) {
        lastContentType = 'resume';
      } else if (lastContentType === 'resume' && !isResumeEnabled) {
        lastContentType = 'rirekisho';
      }

      // 両方無効の場合は空配列を返す
      if (!isRirekishoEnabled && !isResumeEnabled) {
        return [];
      }

      return lastContentType === 'rirekisho'
        ? createMenuItems(rirekishoContent)
        : createMenuItems(mainContent);
    }

    return [];
  }, [location.pathname, previousPath, t, isRirekishoEnabled, isResumeEnabled]);

  // その他のコンテンツのメニューアイテム
  const otherMenuContent = useMemo(
    (): MenuItem[] => [
      {
        label: t('menu.exportImport'),
        path: RouterPath.exportImport,
        translationKey: 'menu.exportImport',
      },
      {
        label: t('menu.initializeInputContent'),
        path: '',
        translationKey: 'menu.initializeInputContent',
      },
      { label: 'FAQ', path: RouterPath.faq },
    ],
    [t]
  );

  return {
    currentMainContent,
    otherMenuContent,
    currentPath: location.pathname,
  };
};
